/**
 * BooleanQuiz Adult Renderer
 * Thin rendering component that uses the useBooleanQuiz headless hook for logic.
 */

import { cn } from '../../../lib/utils'
import { KatexText, QuizResultFeedback, SubmitButton, QUIZ_OX_STYLES, QuizSolveDescription, QuizMedia, QuizTypeBadge, useQuizStyleVariant } from '../shared'
import type { BooleanQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import { useBooleanQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Check, X } from 'lucide-react'

interface BooleanQuizProps {
  quizId: string | number
  quiz: BooleanQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function BooleanQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: BooleanQuizProps) {
  const { t } = useTranslation('quiz')
  const variant = useQuizStyleVariant()
  const oxStyles = QUIZ_OX_STYLES[variant]

  const {
    selected,
    isSolved,
    canSubmit,
    correctAnswer,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSelect,
    handleSubmit,
  } = useBooleanQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* 문제 제목 */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.boolean.label')}
          difficultyLevel={difficultyLevel}
        />
      </motion.div>

      {/* 문제 제목 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <KatexText text={quiz.title} />
        {/* 문제 본문 - formatText로 {image=n} 태그 지원 */}
        {quiz.problem && (
          <div className="mt-4 text-xl">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
        {/* 문제 이미지/영상/Desmos */}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      <div
        className={oxStyles.container}
        role="radiogroup"
        aria-label={t('types.boolean.selectLabel')}
      >
        <motion.button
          role="radio"
          aria-checked={selected === true}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          onClick={() => handleSelect(true)}
          disabled={isSolved || isViewSolved}
          className={cn(
            oxStyles.button,
            // 호버 효과
            !isSolved && !isViewSolved && oxStyles.buttonHover,
            // 선택 상태
            !isSolved && selected === true && oxStyles.buttonSelected,
            // 정답 (정답보기 모드 또는 내가 선택 && 맞음)
            isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) && oxStyles.buttonCorrect,
            // 오답 (맞아요를 선택했는데 정답이 틀려요, 정답보기 모드가 아닐 때만)
            isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers && oxStyles.buttonWrong,
            // 비활성화
            (isSolved || isViewSolved) && 'cursor-not-allowed'
          )}
        >
          <div
            className={cn(
              oxStyles.mark,
              // 선택
              !isSolved && selected === true && oxStyles.markSelected,
              // 정답
              isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) && oxStyles.markCorrect,
              // 오답
              isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers && oxStyles.markWrong
            )}
          >
            {isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) ? (
              <Check className="w-10 h-10" />
            ) : isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers ? (
              <X className="w-10 h-10" />
            ) : (
              'O'
            )}
          </div>
          <span className={cn(
            oxStyles.label,
            !isSolved && selected === true && oxStyles.labelSelected,
            isSolved && correctAnswer === true && (shouldShowCorrectAnswers || selected === true) && oxStyles.labelCorrect,
            isSolved && selected === true && correctAnswer === false && !shouldShowCorrectAnswers && oxStyles.labelWrong
          )}>
            {t('types.boolean.correct')}
          </span>
        </motion.button>

        <motion.button
          role="radio"
          aria-checked={selected === false}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => handleSelect(false)}
          disabled={isSolved || isViewSolved}
          className={cn(
            oxStyles.button,
            // 호버 효과
            !isSolved && !isViewSolved && oxStyles.buttonHover,
            // 선택 상태
            !isSolved && selected === false && oxStyles.buttonSelected,
            // 정답 (정답이 틀려요, 정답보기 모드 또는 내가 선택 && 맞음)
            isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) && oxStyles.buttonCorrect,
            // 오답 (틀려요를 선택했는데 정답이 맞아요, 정답보기 모드가 아닐 때만)
            isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers && oxStyles.buttonWrong,
            // 비활성화
            (isSolved || isViewSolved) && 'cursor-not-allowed'
          )}
        >
          <div
            className={cn(
              oxStyles.mark,
              // 선택
              !isSolved && selected === false && oxStyles.markSelected,
              // 정답 (정답이 틀려요)
              isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) && oxStyles.markCorrect,
              // 오답
              isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers && oxStyles.markWrong
            )}
          >
            {isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) ? (
              <Check className="w-10 h-10" />
            ) : isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers ? (
              <X className="w-10 h-10" />
            ) : (
              'X'
            )}
          </div>
          <span className={cn(
            oxStyles.label,
            !isSolved && selected === false && oxStyles.labelSelected,
            isSolved && correctAnswer === false && (shouldShowCorrectAnswers || selected === false) && oxStyles.labelCorrect,
            isSolved && selected === false && correctAnswer === true && !shouldShowCorrectAnswers && oxStyles.labelWrong
          )}>
            {t('types.boolean.incorrect')}
          </span>
        </motion.button>
      </div>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 결과 피드백 */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={shouldShowCorrectAnswers ? (correctAnswer ? t('types.boolean.correct') : t('types.boolean.incorrect')) : undefined}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 해설 */}
      {isSolved && solveResult?.description && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult.description}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
